<script setup lang="ts">
import { nextTick, watch } from "vue";
import { useAccessStore } from "@/stores/access";
import { ACCESS_TARGETS, openSignIn, type AccessOrigin } from "@/api/access";
import { Button } from "@/components/ui/button";

// REQ-CD-P1-CON-10 / D-CD-60 — a visible, persistent, per-origin sign-in
// state. Renders nothing (no wrapper element) when nothing is flagged.
const access = useAccessStore();

function host(origin: AccessOrigin): string {
  // §14 item 2 — a misconfigured build can leave baseUrl "" (no dev-default
  // fallback); new URL("") throws, and this row must still render rather
  // than crash the whole shell.
  try {
    return new URL(ACCESS_TARGETS[origin].baseUrl).host;
  } catch {
    return ACCESS_TARGETS[origin].baseUrl;
  }
}

function label(origin: AccessOrigin): string {
  return ACCESS_TARGETS[origin].label;
}

// A banner row appearing never moves focus. If focus is inside a row when
// that row is removed, focus moves to #main-content.
watch(
  () => access.requiredOrigins,
  (current, previous) => {
    const removed = previous?.filter((o) => !current.includes(o)) ?? [];
    if (removed.length === 0) return;
    const active = document.activeElement;
    const stillInsideRemovedRow = removed.some((origin) => {
      const row = document.getElementById(`access-banner-row-${origin}`);
      return row?.contains(active) ?? false;
    });
    if (stillInsideRemovedRow) {
      void nextTick(() => {
        document.getElementById("main-content")?.focus();
      });
    }
  },
);
</script>

<template>
  <section v-if="access.requiredOrigins.length > 0" aria-labelledby="access-banner-title">
    <h2 id="access-banner-title" class="sr-only">Sign-in required</h2>
    <div
      v-for="origin in access.requiredOrigins"
      :id="`access-banner-row-${origin}`"
      :key="origin"
      class="flex flex-col gap-3 border-b border-status-degraded/60 bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p class="text-sm text-slate-100">
          <template v-if="access.required[origin] === 0">
            <strong>{{ label(origin) }}: not signed in.</strong>
            The Console could not get a response from {{ host(origin) }}. This usually means this browser has no
            Cloudflare Access sign-in for that address yet; it can also mean the {{ label(origin) }} is down.
          </template>
          <template v-else>
            <strong>{{ label(origin) }}: access refused (HTTP {{ access.required[origin] }}).</strong>
            {{ host(origin) }} received no valid Cloudflare Access identity. Sign in; if this still appears after
            signing in, the {{ label(origin) }} Access policy needs checking.
          </template>
        </p>
        <p class="mt-1 text-xs text-brand-light">
          Opens in a new tab. Sign in there, then come back to this tab — the Console checks again automatically.
        </p>
      </div>
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button class="min-h-[44px] w-full sm:w-auto" @click="openSignIn(origin)">
          Sign in to {{ label(origin) }}<span class="sr-only"> (opens in a new tab)</span>
        </Button>
        <Button
          variant="outline"
          class="min-h-[44px] w-full sm:w-auto"
          :disabled="access.checking"
          @click="access.recheck()"
        >
          Check again
        </Button>
      </div>
    </div>
  </section>
</template>
